import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Upload, ChevronRight, AlertTriangle, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * RCM import wizard — UI skeleton modelled on /ai-generated-sofs/rcm-import-mock.html.
 * Four-step flow:
 *   1. Pick scope (Process / ITGC) + fiscal year, download platform template
 *   2. Upload .xlsx / .csv
 *   3. Validate + reconcile (badges for unknown risks / processes / controls)
 *   4. Commit summary
 *
 * No backend wiring yet — the upload + reconciliation interactions toast
 * "Coming soon" so reviewers can navigate the flow and confirm shape
 * without us building the backend importer first.
 */
function RcmImport() {
  const [step, setStep] = useState(1);
  const [scope, setScope] = useState('process'); // 'process' | 'itgc'
  const [year, setYear] = useState(new Date().getFullYear());
  const [filename, setFilename] = useState('');
  const [policy, setPolicy] = useState('guided'); // 'guided' | 'strict'

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setFilename(f.name);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="text-xs text-gray-500 mb-3">
          SOX / <Link className="text-teal-700 hover:underline" to="/risk-control-matrix">RCM</Link> / Import
        </div>

        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Shield className="h-6 w-6 text-teal-600 shrink-0" aria-hidden />
          Import RCM
        </h1>
        <p className="text-sm text-gray-600 mt-1 max-w-xl">
          Upload a file that matches the platform template (Process RCM or ITGC). Validation
          ties each row to a process area, risk, and control already in your org — or you resolve
          gaps in a reconciliation step.
        </p>

        {/* Stance callout */}
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 leading-relaxed">
          <strong>Product stance:</strong> we don&rsquo;t silently create live risks / controls
          from a cold RCM file. Pick an org policy below: <strong>Strict</strong> (every
          reference must pre-exist) or <strong>Guided</strong> (staging + reconciliation, with
          options to create drafts, link to existing, or skip).
        </div>

        {/* Steps strip */}
        <div className="mt-5 flex flex-wrap gap-2 text-xs font-semibold text-gray-500">
          {[
            { id: 1, label: '1 · Template & type' },
            { id: 2, label: '2 · Upload' },
            { id: 3, label: '3 · Validate & reconcile' },
            { id: 4, label: '4 · Commit' },
          ].map((s) => (
            <span
              key={s.id}
              className={`px-3 py-1.5 rounded-md border ${
                step >= s.id
                  ? 'bg-teal-50 border-teal-200 text-teal-700'
                  : 'bg-white border-gray-200 text-gray-500'
              }`}
            >
              {s.label}
            </span>
          ))}
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <section className="mt-5 bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="text-sm font-bold text-gray-900 mb-3">1. RCM type & fiscal year</h2>
            <p className="text-xs text-gray-500 mb-3">Template columns differ slightly (e.g. SOX cycle vs ITGC domain).</p>
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">RCM scope</label>
                <Segment
                  value={scope}
                  onChange={setScope}
                  options={[
                    { value: 'process', label: 'Process (financial)' },
                    { value: 'itgc', label: 'ITGC' },
                  ]}
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Fiscal year</label>
                <select
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  className="px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {[year, year - 1, year - 2, year - 3].map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-5 pt-4 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-700 mb-1">Download empty platform template</p>
              <p className="text-[11px] text-gray-500 mb-2">Includes header row, column hints, and scope=process|itgc in metadata sheet.</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => toast('Template download coming soon', { icon: 'ℹ️' })}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Process RCM template (.xlsx)
                </button>
                <button
                  type="button"
                  onClick={() => toast('Template download coming soon', { icon: 'ℹ️' })}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  ITGC RCM template (.xlsx)
                </button>
              </div>
            </div>

            <div className="mt-5">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700"
              >
                Continue to upload <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </section>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <section className="mt-5 bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="text-sm font-bold text-gray-900 mb-3">2. Upload file</h2>
            <p className="text-xs text-gray-500 mb-3">
              Accepts <strong>.xlsx</strong> or <strong>.csv</strong> (UTF-8). Max 20 MB.
            </p>
            <label className="block border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-teal-400 hover:bg-teal-50/30">
              <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
              <p className="text-xs text-gray-500">
                Drop file here or <span className="text-teal-700 font-semibold">browse</span>
              </p>
              <p className="text-[11px] text-gray-400 mt-1">{filename || 'No file selected'}</p>
              <input type="file" accept=".xlsx,.csv" onChange={onFile} className="hidden" />
            </label>
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                disabled={!filename}
                onClick={() => setStep(3)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:bg-teal-300 disabled:cursor-not-allowed"
              >
                Validate <ChevronRight className="h-4 w-4" />
              </button>
              <span className="text-[11px] text-gray-500">
                {scope === 'itgc' ? 'ITGC RCM' : 'Process RCM'} · {year}
              </span>
            </div>
          </section>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <section className="mt-5 bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="text-sm font-bold text-gray-900 mb-3">3. Validation & reconciliation</h2>
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900 mb-3">
              <strong>48 rows</strong> structurally valid · <strong>3 rows</strong> need
              resolution · Match policy: <strong>{policy === 'guided' ? 'Guided (reconciliation on)' : 'Strict (all refs must exist)'}</strong>
              <div className="mt-1 text-[11px] text-emerald-700">
                Toggle below to see how Strict would have blocked the job entirely.
              </div>
            </div>
            <div className="mb-3">
              <Segment
                value={policy}
                onChange={setPolicy}
                options={[
                  { value: 'guided', label: 'Guided (reconcile)' },
                  { value: 'strict', label: 'Strict (all refs must exist)' },
                ]}
              />
            </div>

            {policy === 'guided' ? (
              <div className="border border-gray-200 rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Row</th>
                      <th className="px-3 py-2 text-left">Issue</th>
                      <th className="px-3 py-2 text-left">Field</th>
                      <th className="px-3 py-2 text-left">Template value</th>
                      <th className="px-3 py-2 text-left">Resolution</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {SAMPLE_RECON.map((r) => (
                      <tr key={r.row} className="bg-white">
                        <td className="px-3 py-2 text-gray-700">{r.row}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                            r.severity === 'err' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {r.issue}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-blue-700">{r.field}</td>
                        <td className="px-3 py-2 text-gray-700">{r.value}</td>
                        <td className="px-3 py-2">
                          <select
                            defaultValue={r.resolutions[0]}
                            className="px-2 py-1 text-[11px] bg-gray-50 border border-gray-200 rounded"
                          >
                            {r.resolutions.map((opt) => (
                              <option key={opt}>{opt}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="rounded-md bg-red-50 border border-red-200 p-3 text-xs text-red-900 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <strong>Strict mode:</strong> this upload would be blocked with 3 reference errors.
                  Fix the library (create the missing rows in platform) and re-upload, or switch to Guided.
                  <div className="mt-1">
                    <Link to="/risk-control-matrix/org-library" className="text-red-800 underline">Open org library</Link>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setStep(4)}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700"
              >
                Apply & continue
              </button>
              <Link
                to="/risk-control-matrix/org-library"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Pre-load library first
              </Link>
            </div>
          </section>
        )}

        {/* Step 4 */}
        {step === 4 && (
          <section className="mt-5 bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Commit complete
            </h2>
            <p className="text-sm text-emerald-700">
              <strong>45 rows</strong> written to RCM ({year} · {scope === 'itgc' ? 'ITGC' : 'Process'}) · <strong>3 rows</strong> skipped or saved as draft per reconciliation.
            </p>
            <p className="text-xs text-gray-500 mt-2">Audit log: import job <span className="font-mono">IMP-{year}-001</span></p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to="/risk-control-matrix"
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700"
              >
                View RCM
              </Link>
              <Link
                to="/risks"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Review new draft risks
              </Link>
            </div>
          </section>
        )}

        <p className="text-[11px] text-gray-400 mt-6 leading-relaxed">
          <strong>Template contract:</strong>{' '}
          <span className="font-mono">fiscal_year</span>,{' '}
          <span className="font-mono">sox_process_key | itgc_domain</span>,{' '}
          <span className="font-mono">sub_process</span>,{' '}
          <span className="font-mono">risk_ref</span>,{' '}
          <span className="font-mono">control_ref</span>, assertion flags, 5W fields. One row =
          one risk-control pairing for that year; the same risk may repeat across rows with
          different controls.
        </p>
      </div>
    </div>
  );
}

const SAMPLE_RECON = [
  {
    row: 12,
    severity: 'err',
    issue: 'Unknown risk',
    field: 'risk_ref',
    value: 'RSK_442',
    resolutions: ['Create draft risk (goes to approval)', 'Map to RSK_001 (manual merge)', 'Skip row'],
  },
  {
    row: 19,
    severity: 'warn',
    issue: 'Process key',
    field: 'sox_process_key',
    value: 'OTC-RETURNS',
    resolutions: ['Add to catalog as draft', 'Map to "Order to cash"', 'Skip row'],
  },
  {
    row: 31,
    severity: 'err',
    issue: 'Unknown control',
    field: 'control_ref',
    value: 'CTL-999',
    resolutions: ['Map to CTL-205', 'Create draft control', 'Skip row'],
  },
];

function Segment({ value, onChange, options }) {
  return (
    <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5 gap-0.5">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`px-3 py-1.5 text-xs font-semibold rounded ${
              active ? 'bg-teal-50 text-teal-700' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export default RcmImport;
